import type { AgentJob, AgentJobRun, JobWithLatestRun } from '@/lib/api/endpoints/agent-jobs';
import type { NotificationToastInput } from './notify';

/**
 * Turn a finished agent run into a notification toast. `done` → a "finished"
 * toast (with the run's summary/artifact label if present); `failed` → a
 * couldn't-finish toast. Other statuses produce nothing.
 */
export function buildRunToast(job: AgentJob, run: AgentJobRun): NotificationToastInput | null {
  if (run.status === 'done') {
    return {
      id: `run-${run.id}`,
      actor: 'Automation',
      title: job.title,
      message: run.resultSummary ?? run.artifactLabel ?? 'Finished — tap View to see the result.',
      actionLabel: 'View',
    };
  }
  if (run.status === 'failed') {
    return {
      id: `run-${run.id}`,
      actor: 'Automation · failed',
      title: job.title,
      message: run.error ?? 'The run couldn’t finish.',
      actionLabel: 'View',
    };
  }
  return null;
}

/** How recently a run must have completed to notify when we first see it already
 *  terminal (guards against alerting for old runs that load in later). */
const RECENT_MS = 2 * 60 * 1000;

export interface RunCompletionResult {
  /** Toasts to fire for newly-completed runs. */
  fires: NotificationToastInput[];
  /** The run-id → status map to remember for the next diff. */
  next: Map<string, string>;
}

/**
 * Diff the latest jobs against the previously-seen run statuses and return the
 * toasts to fire for runs that just completed.
 *
 * `seen === null` means "first load / baseline": record current statuses but fire
 * nothing (so pre-existing finished runs don't pop a toast on app open). After
 * that, a toast fires when a run we were watching go `queued`/`running` turns
 * terminal, or when an unseen run appears terminal AND completed very recently.
 */
export function detectRunCompletions(
  items: JobWithLatestRun[],
  seen: Map<string, string> | null,
  nowMs: number,
): RunCompletionResult {
  const next = new Map<string, string>();
  const fires: NotificationToastInput[] = [];

  for (const item of items) {
    const run = item.latestRun;
    if (!run) continue;
    next.set(run.id, run.status);
    if (seen === null) continue; // baseline — populate only

    const isTerminal = run.status === 'done' || run.status === 'failed';
    if (!isTerminal) continue;

    const prev = seen.get(run.id);
    if (prev === run.status) continue; // already handled this terminal state

    const wasActive = prev === 'queued' || prev === 'running';
    const recentlyDone = run.completedAt ? nowMs - Date.parse(run.completedAt) < RECENT_MS : false;
    if (wasActive || (prev === undefined && recentlyDone)) {
      const toast = buildRunToast(item.job, run);
      if (toast) fires.push(toast);
    }
  }

  return { fires, next };
}
